import { useCrmLocale } from '../../ui/locale/crmLocale';
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Menu, X, CircleHelp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCrmSidebar, useCrmShellPresentation } from './CrmSidebarContext';
import { useCrmSidebarMobile } from './useCrmSidebarMobile';
import { cycleOverlayTab } from '../../ui/overlayFocus';
import './crm-navigation-sidebar.css';

const CrmHelpPanel = lazy(() => import('./CrmHelpPanel'));

/** Shared presentation only. Departments retain their navigation and authorization. */
export default function CrmNavigationSidebar({ children, label, collapsed: controlled, onToggle, renderHelp, navigationHeader }: {
  children: ReactNode | ((compact: boolean, closeNavigation: () => void) => ReactNode);
  label: string;
  collapsed?: boolean;
  onToggle?: () => void;
  renderHelp?: (onClose: () => void) => ReactNode;
  navigationHeader?: ReactNode;
}) {
  const { t: crmText } = useCrmLocale();

  const mobile = useCrmSidebarMobile();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLButtonElement>(null);
  const { pilot, registerHelp } = useCrmShellPresentation();
  const closeHelp = () => {
    setHelpOpen(false);
    requestAnimationFrame(() => (registerHelp ? document.querySelector<HTMLButtonElement>('.crm-profile-button') : mobile ? document.querySelector<HTMLButtonElement>('.crm-header-menu') : helpRef.current)?.focus({preventScroll:true}));
  };
  const { language } = useLanguage();
  const location = useLocation();
  useEffect(() => { setHelpOpen(false); }, [location.key]);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const collapsed = controlled ?? (mobile ? !mobileOpen : desktopCollapsed);
  const toggle = () => onToggle ? onToggle() : mobile ? setMobileOpen(value => !value) : setDesktopCollapsed(value => !value);
  const close = () => { if (controlled !== undefined) { if (!collapsed) onToggle?.(); } else setMobileOpen(false); };
  useCrmSidebar(toggle);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(close);
  const previousLocation = useRef(location.key);
  const previousMobile = useRef(mobile);
  closeRef.current = close;
  // Keep Help rendered by its department owner so role/tab/access context stays live.
  const helpOpener = useRef<() => void>(() => undefined);
  useEffect(() => { helpOpener.current = () => { if (mobile) closeRef.current(); setHelpOpen(true); }; }, [mobile]);
  useEffect(() => {
    if (!registerHelp) return;
    return registerHelp(() => helpOpener.current());
  }, [registerHelp]);
  useEffect(() => {
    if (mobile && (previousLocation.current !== location.key || previousMobile.current !== mobile)) closeRef.current();
    previousLocation.current = location.key;
    previousMobile.current = mobile;
  }, [location.key, mobile]);
  useLayoutEffect(() => {
    if (!mobile || collapsed) return;
    const dialog = dialogRef.current;
    const trigger = document.querySelector<HTMLButtonElement>('.crm-header-menu');
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    dialog?.showModal();
    dialog?.querySelector<HTMLButtonElement>('.crm-sidebar-toggle')?.focus();
    return () => {
      dialog?.close();
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      queueMicrotask(() => { if (trigger?.isConnected && !dialog?.open) trigger.focus(); });
    };
  }, [mobile, collapsed]);
  const compact = !mobile && collapsed;
  const content = <aside className={`crm-shared-sidebar ${compact ? 'crm-shared-sidebar--compact' : 'crm-shared-sidebar--expanded'}`} aria-label={label}>
    <div className="crm-sidebar-controls">
      <button type="button" className="crm-sidebar-toggle" onClick={toggle}
      aria-label={language === 'ar' ? mobile ? crmText("إغلاق القائمة") : compact ? crmText("توسيع القائمة") : crmText("طي القائمة") : mobile ? crmText("Close navigation") : compact ? crmText("Expand navigation") : crmText("Collapse navigation")}>
      {mobile ? <X size={18} /> : pilot ? <Menu size={22} /> : (compact !== (language === 'ar')) ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
    </button></div>
    {navigationHeader && <div className="crm-sidebar-context">{navigationHeader}</div>}
    <div className="crm-sidebar-navigation">{typeof children === 'function' ? children(compact, () => { if (mobile) close(); }) : children}</div>
    {!registerHelp && <div className="crm-sidebar-footer"><button ref={helpRef} type="button" className="crm-sidebar-help" aria-label={crmText("Help")} title={crmText("Help")} aria-haspopup="dialog" aria-expanded={helpOpen} onClick={()=>{if(mobile)close();setHelpOpen(true);}}>
      <CircleHelp size={20} aria-hidden="true"/>{!compact && <span>{crmText("Help")}</span>}
    </button></div>}
  </aside>;
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Native dialog provides Escape and modality; click only dismisses the backdrop.
  const navigation = mobile ? <dialog ref={dialogRef} className="crm-sidebar-dialog" aria-label={label}
    onKeyDown={cycleOverlayTab}
    onCancel={event => { event.preventDefault(); close(); }}
    onClick={event => { if (event.target === event.currentTarget) close(); }}>{content}</dialog> : content;
  return <>{navigation}{helpOpen && <div onKeyDownCapture={event => {
    if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); closeHelp(); }
  }}><Suspense fallback={<span role="status">{crmText("Loading help…")}</span>}>
    {renderHelp ? renderHelp(closeHelp) : <CrmHelpPanel onClose={closeHelp}/>}
  </Suspense></div>}</>;
}
